import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocalStorage } from 'shared/hooks/use-local-storage';
import { useQueryParams } from 'shared/hooks/use-query-params';
import { AsyncState, deriveAsyncState } from 'shared/utils/async-state';
import { getOffsetLimit } from 'shared/utils/pagination/page-math';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FilterParamConfig<T = any> = {
  key: string;
  defaultValue: T;
  parser?: (raw: string) => T;
  serializer?: (value: T) => string;
};

type FiltersConfig = Record<string, FilterParamConfig>;

type FilterValues<F extends FiltersConfig> = {
  [K in keyof F]: F[K] extends FilterParamConfig<infer U> ? U : never;
};

/** Minimal shape that any RTK Query hook satisfies. */
type UseQueryHook<TData, TArgs> = (
  args: TArgs,
  options?: { skip?: boolean },
) => {
  /** Последний успешный ответ для **любого** аргумента хука. */
  data?: TData[];
  /**
   * Ответ строго для текущего аргумента: `undefined`, пока новый запрос в пути.
   * Именно эта пара и позволяет отличить «первая загрузка» от «обновляем показанное».
   */
  currentData?: TData[];
  isFetching: boolean;
  isError: boolean;
  error?: unknown;
};

export interface IUseOffsetPaginationProps<TData, TArgs extends Record<string, any>, F extends FiltersConfig> {
  /** RTK Query useXxxQuery hook */
  useQueryHook: UseQueryHook<TData, TArgs>;
  /** Static args always merged into the query (e.g., { tournamentId }) */
  baseArgs?: Partial<TArgs>;
  /** Map current filter values to query arg fields (excluding limit/offset) */
  mapFiltersToArgs: (filters: FilterValues<F>) => Partial<TArgs>;
  /** Filter param configs for URL sync */
  filtersConfig?: F;
  /**
   * Значения фильтров, которые пользователь уже ввёл, но которые ещё не доехали до
   * URL (дебаунс). Нужны, чтобы индикатор загорался на первом символе, а не через
   * полсекунды: между нажатием клавиши и стартом запроса `isFetching` ещё `false`.
   *
   * Сравниваются не сырые значения, а аргументы запроса, посчитанные от черновика и
   * от закоммиченных фильтров. Так ввод, который запрос не изменит (например, строка
   * короче минимальной длины поиска), индикатор честно не зажигает.
   */
  draftFilters?: Partial<FilterValues<F>>;
  /** Ждём чего-то ещё до запроса — например, проверки авторизации. */
  isPending?: boolean;
  /** localStorage key for perPage. Default: 'perPage' */
  perPageStorageKey?: string;
  /** Default items per page. Default: 10 */
  defaultPerPage?: number;
  /** Return `{ skip: true }` to prevent the query from running */
  queryOptions?: (args: TArgs) => { skip?: boolean };
}

export interface UseOffsetPaginationResult<TData, F extends FiltersConfig> {
  /** Items for the current page */
  items: TData[];
  /** Какую ветку рисовать и надо ли пометить показанное устаревшим. */
  state: AsyncState;
  /**
   * Сырой `isFetching`.
   *
   * @deprecated Поднимается на каждом рефетче, поэтому по нему нельзя переключать
   * ветку рендера — список схлопнется на каждом нажатии клавиши в поиске. Для нового
   * кода используйте `state`, см. `.claude/docs/async-loading-states.md`.
   */
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Current page number (1-based, from URL) */
  page: number;
  /** Current items-per-page (from localStorage) */
  limit: number;
  /** Highest page number that has been fetched with data */
  maxLoadedPage: number;
  /** False when the frontier page returned fewer items than the limit */
  hasMorePage: boolean;
  /** maxLoadedPage + (hasMorePage ? 1 : 0) — includes the pseudo page */
  totalPages: number;
  /** Current filter values (from URL) */
  filters: FilterValues<F>;
  setPage: (page: number) => void;
  setFilters: (updates: Partial<FilterValues<F>>) => void;
  setPerPage: (perPage: number) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOffsetPagination<TData, TArgs extends Record<string, any>, F extends FiltersConfig = Record<string, never>>(
  props: IUseOffsetPaginationProps<TData, TArgs, F>,
): UseOffsetPaginationResult<TData, F> {
  const {
    useQueryHook,
    baseArgs,
    mapFiltersToArgs,
    filtersConfig,
    draftFilters,
    isPending,
    perPageStorageKey = 'perPage',
    defaultPerPage = 10,
    queryOptions,
  } = props;

  // ---- URL state (page + filters) ----------------------------------------

  const queryParamsConfig = useMemo(() => {
    const cfg: Record<string, any> = {
      page: { key: 'page', defaultValue: 1, parser: Number },
    };
    if (filtersConfig) {
      for (const [name, fc] of Object.entries(filtersConfig)) {
        cfg[name] = fc;
      }
    }
    return cfg;
  }, []); // filtersConfig is expected to be a module-scope constant

  const [params, setParams] = useQueryParams(queryParamsConfig);

  const page: number = (params as any).page ?? 1;

  const filters = useMemo(() => {
    const result: Record<string, any> = {};
    if (filtersConfig) {
      for (const name of Object.keys(filtersConfig)) {
        result[name] = (params as any)[name];
      }
    }
    return result as FilterValues<F>;
  }, [params, filtersConfig]);

  // ---- localStorage (perPage) ---------------------------------------------

  const storageConfig = useMemo(
    () => ({
      perPage: { key: perPageStorageKey, defaultValue: defaultPerPage, parser: Number },
    }),
    [], // storage key is stable
  );

  const [storage, setStorage] = useLocalStorage(storageConfig);
  const perPage: number = storage.perPage;

  // ---- Scope tracking (reset on filter / perPage change) ------------------

  const scopeKey = useMemo(() => JSON.stringify({ filters, perPage }), [filters, perPage]);
  const prevScopeKeyRef = useRef(scopeKey);

  const [paginationState, setPaginationState] = useState({
    maxLoadedPage: 0,
    hasMorePage: true,
  });

  useEffect(() => {
    if (prevScopeKeyRef.current !== scopeKey) {
      prevScopeKeyRef.current = scopeKey;
      setPaginationState({ maxLoadedPage: 0, hasMorePage: true });
      if (page !== 1) {
        setParams({ page: 1 } as any);
      }
    }
  }, [scopeKey, page, setParams]);

  // ---- Build query args & call RTK Query ----------------------------------

  const { offset, limit } = getOffsetLimit(page, perPage);

  const queryArgs = useMemo(() => {
    const filterArgs = mapFiltersToArgs(filters);
    return { ...baseArgs, ...filterArgs, limit, offset } as unknown as TArgs;
  }, [baseArgs, mapFiltersToArgs, filters, limit, offset]);

  const options = queryOptions?.(queryArgs);
  const { data, currentData, isFetching, isError, error } = useQueryHook(queryArgs, options);

  // ---- Pending input (debounced filters not yet in the URL) ----------------

  const isInputPending = useMemo(() => {
    if (!draftFilters) return false;

    const draft = mapFiltersToArgs({ ...filters, ...draftFilters } as FilterValues<F>);
    const committed = mapFiltersToArgs(filters);
    // Тот же приём сравнения, что и у `scopeKey` выше.
    return JSON.stringify(draft) !== JSON.stringify(committed);
  }, [draftFilters, filters, mapFiltersToArgs]);

  // ---- Update pagination state after fetch --------------------------------

  useEffect(() => {
    if (isFetching) return;
    if (data === undefined) return;
    // Only update when current page advances or matches the frontier
    if (page < paginationState.maxLoadedPage) return;

    const receivedCount = data.length;
    const hasMore = receivedCount >= limit && receivedCount > 0;

    setPaginationState((prev) => ({
      maxLoadedPage: Math.max(prev.maxLoadedPage, page),
      hasMorePage: hasMore,
    }));
  }, [isFetching, data, page, limit, paginationState.maxLoadedPage]);

  // ---- Derived values -----------------------------------------------------

  const { maxLoadedPage, hasMorePage } = paginationState;
  const totalPages = maxLoadedPage + (hasMorePage ? 1 : 0);
  // Намеренно `data`, а не `currentData`: пока летит запрос под новым аргументом, на
  // экране должен остаться прошлый ответ. Что он устарел, скажет `state.isRefreshing`.
  const items: TData[] = data ?? [];

  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: items.length,
        hasCurrentData: currentData !== undefined,
        isFetching,
        isPending: isPending || isInputPending,
        isError,
      }),
    [items.length, currentData, isFetching, isPending, isInputPending, isError],
  );

  // ---- Callbacks ----------------------------------------------------------

  const setPage = useCallback(
    (newPage: number) => {
      setParams({ page: newPage } as any);
    },
    [setParams],
  );

  const setFilters = useCallback(
    (updates: Partial<FilterValues<F>>) => {
      setParams({ ...updates, page: 1 } as any);
    },
    [setParams],
  );

  const setPerPage = useCallback(
    (newPerPage: number) => {
      setStorage({ perPage: newPerPage } as any);
      setParams({ page: 1 } as any);
    },
    [setStorage, setParams],
  );

  // ---- Return -------------------------------------------------------------

  return {
    items,
    state,
    isLoading: isFetching,
    isError,
    error: error ?? null,
    page,
    limit: perPage,
    maxLoadedPage,
    hasMorePage,
    totalPages,
    filters,
    setPage,
    setFilters,
    setPerPage,
  };
}
